import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Shield } from 'lucide-react';import { AppFooter } from '@/components/layout/AppFooter';
import { BrandLogo } from '@/components/BrandLogo';

export default function TermsPage() {
  return (
    <div data-testid="terms-page" className="min-h-screen bg-background">
      <nav className="border-b border-border bg-background/80 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 flex h-16 items-center gap-3">
          <BrandLogo width={180} />
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Terms of Service</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: March 1, 2026 | Version 1.0</p>

        <div className="prose prose-sm max-w-none space-y-6 text-foreground">
          <section>
            <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>1. Service Description</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">VendorSuite ("the Platform") provides AI-powered vendor compliance automation software for property management companies. The Platform automates document ingestion, AI extraction, compliance scoring, vendor chasing, and reporting.</p>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>2. No Guarantee — Human-in-the-Loop Required</h2>
            <Card className="border-[hsl(36_80%_76%)] bg-[hsl(36_100%_96%)]">
              <CardContent className="p-4">
                <p className="text-sm font-medium text-[hsl(28,95%,26%)] leading-relaxed">The Platform provides automated suggestions only. The final compliance decision rests solely with the Property Manager (the "User"). VendorSuite does not guarantee the accuracy, completeness, or timeliness of any AI-generated analysis, extraction, or compliance determination. Users must independently verify all data before making compliance decisions.</p>
              </CardContent>
            </Card>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>3. AI Accuracy Disclaimer</h2>
            <Card className="border-destructive/30 bg-destructive/5">
              <CardContent className="p-4">
                <p className="text-sm font-medium leading-relaxed">AI-extracted data may contain errors. Users must verify all policy expiration dates and coverage limits before granting vendor access to property sites. VendorSuite shall not be held liable for any damages, losses, or liabilities arising from reliance on AI-generated data without independent verification.</p>
              </CardContent>
            </Card>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>4. Liability Cap</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">TO THE MAXIMUM EXTENT PERMITTED BY LAW, VENDORSUITE'S TOTAL AGGREGATE LIABILITY ARISING OUT OF OR IN CONNECTION WITH THIS AGREEMENT SHALL NOT EXCEED THE TOTAL AMOUNT PAID BY THE USER FOR THE SOFTWARE DURING THE TWELVE (12) MONTH PERIOD IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM (i.e., the base subscription fee paid for the applicable plan).</p>
            <p className="text-sm leading-relaxed text-muted-foreground mt-2">IN NO EVENT SHALL VENDORSUITE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES.</p>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>5. Subscription & Billing</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">Subscriptions are billed monthly. All fees are non-refundable except as required by applicable law. The Platform offers Professional and Enterprise plans, each consisting of a flat monthly fee plus a per-active-vendor usage fee. Cancellation takes effect at the end of the current billing period.</p>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>6. User Responsibilities</h2>
            <ul className="text-sm space-y-2 text-muted-foreground list-disc ml-6">
              <li>Users are responsible for the accuracy and legality of all data uploaded to the Platform.</li>
              <li>Users must verify AI-extracted data before making compliance decisions.</li>
              <li>Users must maintain the confidentiality of their account credentials.</li>
              <li>Users must comply with all applicable local, state, and federal regulations regarding vendor compliance and data handling.</li>
            </ul>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>7. Data Processing</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">Uploaded documents are processed by third-party AI sub-processors including OpenAI (for text extraction and analysis) and Amazon Web Services (for document storage and OCR). By using the Platform, you consent to the processing of your data by these sub-processors in accordance with our Privacy Policy.</p>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>8. Governing Law</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">These Terms shall be governed by and construed in accordance with the laws of the State of Florida, without regard to its conflict of law provisions.</p>
          </section>

          <section className="mt-8">
            <p className="text-xs text-muted-foreground">By creating an account or using VendorSuite, you acknowledge that you have read, understood, and agree to these Terms of Service.</p>
          </section>
        </div>
      </div>
      <AppFooter />
    </div>
  );
}
